/**
 * Website-wide image & video protection.
 *
 * Provides the strongest practical CLIENT-SIDE protection against normal
 * user attempts to download, save, drag, copy, share, or open our media
 * through the standard browser / device UI. It is NOT (and cannot be) a
 * DRM-grade defense — a determined user with devtools, screen recording,
 * or the browser cache can still obtain assets. This module explicitly
 * targets the "casual" download vectors:
 *
 *   Desktop:  right-click → Save As / Open in new tab, drag-to-desktop,
 *             middle-click, Ctrl/⌘-click, native <video> download button.
 *   Mobile:   long-press → Save image / Share / Open image callout.
 *
 * Design goals:
 *   • Zero touch on existing images / videos (no attribute rewriting on
 *     existing components).
 *   • Automatic coverage for any future <img>/<video> added anywhere —
 *     including via async data fetch or admin uploads.
 *   • Only touches media elements — normal clicks, scrolling, popup
 *     interactions, form inputs, cart, video playback, PiP, fullscreen,
 *     progress bar, mute button, etc. remain 100 % functional.
 *   • Idempotent — calling init() twice is a no-op.
 *   • Fully reversible via teardown() so it can be scoped to a route
 *     subtree (e.g. disabled on admin pages).
 */

const STATE_KEY = '__sdfMediaProtection';
const STYLE_ID = 'sdf-media-protection-style';
const MARK_ATTR = 'data-sdf-protected';

/** Return true if the given element is an <img>, <video>, <picture>, or <source>. */
function isMedia(el) {
    if (!el || el.nodeType !== 1) return false;
    const t = el.tagName;
    return t === 'IMG' || t === 'VIDEO' || t === 'PICTURE' || t === 'SOURCE';
}

/** Attempt a very subtle haptic on touch devices. Safe no-op if unsupported. */
function subtleHaptic() {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(10);
        }
    } catch (_) { /* silent */ }
}

/** Apply per-element protection attributes. Idempotent. */
function applyProtection(el) {
    if (!isMedia(el)) return;
    if (el.getAttribute(MARK_ATTR) === '1') return;
    if (el.tagName === 'IMG') {
        if (el.getAttribute('draggable') !== 'false') {
            el.setAttribute('draggable', 'false');
        }
    } else if (el.tagName === 'VIDEO') {
        // Remove the browser's native download option from the built-in
        // controls / three-dot menu (Chrome/Edge honour this attribute).
        // We also disable remote playback so Cast / AirPlay does not offer
        // an easy "download" side-channel where present.
        const current = el.getAttribute('controlsList') || '';
        const parts = new Set(current.split(/\s+/).filter(Boolean));
        const before = parts.size;
        parts.add('nodownload');
        parts.add('noremoteplayback');
        if (parts.size !== before) {
            el.setAttribute('controlsList', Array.from(parts).join(' '));
        }
        if (!el.hasAttribute('disableRemotePlayback')) {
            el.setAttribute('disableRemotePlayback', '');
        }
    }
    el.setAttribute(MARK_ATTR, '1');
}

/** Install global CSS that disables selection / drag / touch-callout on media. */
function installStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
        /* Website-wide media protection — applied everywhere EXCEPT admin routes.
           The wrapper toggles the [data-sdf-protect-root='on'] attribute on <html>
           so that turning protection off (e.g. inside admin) removes every rule
           below in a single reflow. */
        html[data-sdf-protect-root='on'] img,
        html[data-sdf-protect-root='on'] video,
        html[data-sdf-protect-root='on'] picture,
        html[data-sdf-protect-root='on'] source {
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            -webkit-touch-callout: none;   /* iOS long-press "Save Image" callout */
            -webkit-user-drag: none;
            -khtml-user-drag: none;
            -moz-user-drag: none;
            -o-user-drag: none;
            user-drag: none;
            -webkit-tap-highlight-color: transparent;
        }
    `;
    document.head.appendChild(style);
}

function removeStyle() {
    const el = document.getElementById(STYLE_ID);
    if (el) el.remove();
}

/** Enable protection globally. Idempotent. */
export function initMediaProtection() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (window[STATE_KEY]?.active) return;

    installStyle();
    document.documentElement.setAttribute('data-sdf-protect-root', 'on');

    // Apply to every existing media element on the page.
    document.querySelectorAll('img, video, picture, source').forEach(applyProtection);

    // ---- Global event blockers (capture phase so they win before React). ----

    const onContextMenu = (e) => {
        if (isMedia(e.target)) {
            e.preventDefault();
            subtleHaptic();
        }
    };

    const onDragStart = (e) => {
        if (isMedia(e.target)) e.preventDefault();
    };

    const onAuxClick = (e) => {
        // Middle-click on media (button 1). Only block when the media is
        // NOT wrapped in an <a> — otherwise we would break the perfectly
        // normal "open link in new tab" behaviour on anchors that happen
        // to contain an image.
        if (e.button === 1 && isMedia(e.target) && !e.target.closest('a')) {
            e.preventDefault();
        }
    };

    const onClick = (e) => {
        // Ctrl/⌘ + click on a bare image would normally open it in a new
        // tab in some browsers. Same anchor-guard as middle-click.
        if ((e.ctrlKey || e.metaKey) && isMedia(e.target) && !e.target.closest('a')) {
            e.preventDefault();
        }
    };

    const onSelectStart = (e) => {
        if (isMedia(e.target)) e.preventDefault();
    };

    document.addEventListener('contextmenu', onContextMenu, true);
    document.addEventListener('dragstart', onDragStart, true);
    document.addEventListener('auxclick', onAuxClick, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('selectstart', onSelectStart, true);

    // ---- Observe the DOM so future <img>/<video> are auto-protected. ----
    // We ONLY watch node additions (childList + subtree). We deliberately
    // do NOT watch attribute mutations: applyProtection() itself mutates
    // `draggable` / `controlsList` on media, which would re-fire the
    // observer and cause an infinite loop that freezes the tab (this was
    // the cause of the post-deploy "stuck loading" regression). The
    // MARK_ATTR guard already keeps re-protection idempotent when React
    // re-renders the same element.

    const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (isMedia(node)) applyProtection(node);
                if (node.querySelectorAll) {
                    node.querySelectorAll('img, video, picture, source').forEach(applyProtection);
                }
            }
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });

    window[STATE_KEY] = {
        active: true,
        cleanup() {
            document.removeEventListener('contextmenu', onContextMenu, true);
            document.removeEventListener('dragstart', onDragStart, true);
            document.removeEventListener('auxclick', onAuxClick, true);
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('selectstart', onSelectStart, true);
            observer.disconnect();
            document.documentElement.removeAttribute('data-sdf-protect-root');
            removeStyle();
            // Best-effort: strip per-element flags so a subsequent init()
            // starts clean.
            document.querySelectorAll(`[${MARK_ATTR}]`).forEach(el => el.removeAttribute(MARK_ATTR));
        },
    };
}

/** Disable protection globally. Safe to call when not active. */
export function teardownMediaProtection() {
    if (typeof window === 'undefined') return;
    const st = window[STATE_KEY];
    if (!st?.active) return;
    st.cleanup();
    window[STATE_KEY] = { active: false };
}
